import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import {
  GridActions,
  Column,
  ColumnKeys,
  IntegrityMonitorRuleGridItem,
  IntegrityMonitorRulesGridWebComponentSchema,
  IntegrityMonitorScopeType,
  IntegrityMonitoringGridEvents,
} from '../types';
import {
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_INTEGRITY_MONITOR_RULES_APP,
} from '../../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { GridTitleConfig } from '../../../../grid/uiElements/gridViewTitle/types';
import {
  booleanRenderer,
  configurationStatusRenderer,
  entityTypeRenderer,
  inlineRowActionsRenderer,
  ruleNameRenderer,
  statusRenderer,
} from './customGridRenderers';
import { defaultPageSizeOptions, SelectionStoreMode } from '../../../../grid/gridView/types';
import { IntegrityMonitorRulesGridDataProvider } from '../dataProvider/IntegrityMonitorRulesGridDataProvider';
import { DefaultSmartViewId, ViewType } from '../../integrityMonitorRulesSmartViews/types';
import { TemplateState } from '../../../../types/common/types';
import { dateRenderer } from '@nirvana/shared/common';
import { GzSelectWebComponent } from '../../../../ui/gzSelect';
import { severityRenderer } from '../../../common/customGridRenderers';
import { Rights } from '@nirvana/shared/constants';

export class IntegrityMonitorRulesGridView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<
      Column,
      ColumnKeys,
      IntegrityMonitorRuleGridItem
    >;
    this.dom.$actionsSelect = this.dom.$gridView.querySelector('#actionsSelect') as GzSelectWebComponent;
    this.dom.$actionsButtonText = this.dom.$gridView.querySelector('#actionsText');

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getIntegrityMonitorStrings().integrityMonitorRules;
  }

  setTranslatedStrings() {
    this.dom.$actionsButtonText.textContent = this.translations.actions;
  }

  setLoadingState(): void {
    this.dom.$gridView.controller.disableDataLoading(true);
    this.dom.$gridView.controller.setMoreItemsLoadingState(true);
  }

  removeLoadingState(): void {
    this.dom.$gridView.controller.disableDataLoading(false);
    this.dom.$gridView.controller.setMoreItemsLoadingState(false);
  }

  buildSuccessView(data: IntegrityMonitorRulesGridWebComponentSchema) {
    this.resetTemplateState(this.dom.$wrapper);
    this.buildGridView(data);
    this.handleActionsSelectVisibility(data.activeViewId);
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public buildGridView(data: IntegrityMonitorRulesGridWebComponentSchema) {
    this.dom.$gridView.dataProvider = new IntegrityMonitorRulesGridDataProvider(
      this.dependencies,
      data.activeView.pageSize
    );
    this.dom.$gridView.propertiesSet = false;
    this.dom.$gridView.viewId = data.activeView.id;
    this.dom.$gridView.allColumnDetails = data.allColumnDetails;
    this.dom.$gridView.columns = data.activeView.displayedColumns;
    this.dom.$gridView.isCompactViewChecked = data.activeView.compactView;
    this.dom.$gridView.filters = data.activeView.filters;
    this.dom.$gridView.pageSize = data.activeView.pageSize;
    this.dom.$gridView.pageNumber = data.activeView.pageNumber;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSizeOptions = defaultPageSizeOptions;
    this.dom.$gridView.extraParams = this.getExtraParams(data);

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.RULE_NAME]: ruleNameRenderer.bind(this),
      [ColumnKeys.CREATED_ON]: dateRenderer.bind(this),
      [ColumnKeys.LAST_MODIFIED_ON]: dateRenderer.bind(this),
      [ColumnKeys.ENTITY_TYPE]: entityTypeRenderer.bind(this),
      [ColumnKeys.SEVERITY]: severityRenderer.bind(this),
      [ColumnKeys.RULE_TYPE]: booleanRenderer.bind(this, {
        positive: this.dependencies.translateFn('Default'),
        negative: this.dependencies.translateFn('Custom'),
      }),
      [ColumnKeys.STATUS]: statusRenderer.bind(this),
      [ColumnKeys.REAL_TIME_MONITORING]: booleanRenderer.bind(this, {
        positive: this.dependencies.translateFn('On#Active'),
        negative: this.dependencies.translateFn('Off'),
      }),
      [ColumnKeys.CONFIG_STATUS]: configurationStatusRenderer.bind(this),
      [ColumnKeys.INLINE_ACTIONS]: inlineRowActionsRenderer.bind(this),
    };

    this.dom.$gridView.predefinedFiltersKeys = data.predefinedFiltersKeys;
    this.dom.$gridView.defaultViewProps = data.defaultViewProps;
    this.dom.$gridView.activeViewProps = data.activeView;
    this.dom.$gridView.activeViewInitialProps = { ...data.activeView };
    this.dom.$gridView.titleConfig = new GridTitleConfig(data.activeView.name);
    this.dom.$gridView.hasSelectableRows = this.dependencies.canModifyRulesAndRuleSets();
    this.dom.$gridView.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
    };
    this.dom.$gridView.showGlobalActions = true;
    this.dom.$gridView.sideMenuParent = window.document
      .querySelector(WEB_COMPONENT_INTEGRITY_MONITOR_RULES_APP)
      ?.shadowRoot?.querySelector('.wrapper');
    this.dom.$gridView.propertiesSet = true;
  }

  public enableActionOption(optionValue: GridActions) {
    const optionIndex = this.getActionOptionIndex(optionValue);
    if (optionIndex >= 0) {
      this.dom.$actionsSelect.options[optionIndex].disabled = false;
    }
  }

  public disableActionOption(optionValue: GridActions) {
    const optionIndex = this.getActionOptionIndex(optionValue);
    if (optionIndex >= 0) {
      this.dom.$actionsSelect.options[optionIndex].disabled = true;
    }
  }

  public updateTitle(newTitle: string) {
    this.dom.$gridView.titleConfig = new GridTitleConfig(newTitle);
  }

  private getExtraParams(data: IntegrityMonitorRulesGridWebComponentSchema): any {
    const extraParams = {
      companyId: this.dependencies.getActiveCompany().companyId,
    } as any;

    switch (data.activeView.type) {
      case ViewType.RULE_SET:
        // The id of a RULE_SET smart view is the Mongo Id of that rule set
        extraParams.ruleSetId = data.activeView.id;
        break;

      case ViewType.DEFAULT_OS_RULES:
        extraParams.scopeType = IntegrityMonitorScopeType.OS;
        extraParams.isDefaultRule = true;
        break;

      case ViewType.DEFAULT_APPLICATION_RULES:
        extraParams.scopeType = IntegrityMonitorScopeType.APPLICATION;
        extraParams.isDefaultRule = true;
        break;

      case ViewType.CUSTOM_RULES:
        extraParams.scopeType = IntegrityMonitorScopeType.CUSTOM;
        extraParams.isDefaultRule = false;
        break;

      case ViewType.ALL_RULES:
        // When All Rules view is selected, it is possible that a special filter is applied
        if (data.ruleIdsFilter.length > 0) {
          extraParams.ruleIdsFilter = data.ruleIdsFilter;
        }
        break;
    }

    return extraParams;
  }

  private handleActionsSelectVisibility(activeViewId: string) {
    const disabled = !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]);
    this.dom.$actionsSelect.disabled = disabled;
    if (!this.dependencies.canModifyRulesAndRuleSets()) {
      // Hide Actions selector
      this.dom.$actionsSelect.classList.add('hidden');
      return;
    }

    // Display Actions selector
    this.dom.$actionsSelect.classList.remove('hidden');
    this.buildActionsSelect(activeViewId);
  }

  private buildActionsSelect(activeViewId: string) {
    switch (activeViewId) {
      case DefaultSmartViewId.ALL_RULES:
      case DefaultSmartViewId.CUSTOM_RULES:
        this.dom.$actionsSelect.options = [
          {
            title: this.translations.newRule,
            value: GridActions.NEW_RULE,
          },
          {
            title: this.translations.newRuleSet,
            value: GridActions.NEW_RULE_SET,
          },
          {
            title: this.translations.assignToRuleSets,
            value: GridActions.ASSIGN_RULES_TO_RULE_SETS,
          },
          {
            title: this.translations.deleteRule,
            value: GridActions.DELETE_RULES,
            customClass: 'danger',
          },
        ];
        break;

      case DefaultSmartViewId.DEFAULT_OS_RULES:
      case DefaultSmartViewId.DEFAULT_APPLICATION_RULES:
        this.dom.$actionsSelect.options = [
          {
            title: this.translations.newRuleSet,
            value: GridActions.NEW_RULE_SET,
          },
          {
            title: this.translations.assignToRuleSets,
            value: GridActions.ASSIGN_RULES_TO_RULE_SETS,
          },
        ];
        break;

      default:
        // When viewing a specific rule set (id: Rule_set_name)
        this.dom.$actionsSelect.options = [
          {
            title: this.translations.newRule,
            value: GridActions.NEW_RULE,
          },
          {
            title: this.translations.unassignRules,
            value: GridActions.UNASSIGN_RULES_FROM_RULE_SETS,
          },
        ];
    }
  }

  private getActionOptionIndex(optionValue: GridActions): number {
    return this.dom.$actionsSelect.options.findIndex(option => option.value === optionValue);
  }

  private onClickOnRuleName(ruleData: IntegrityMonitorRuleGridItem): void {
    const eventType = ruleData.isDefaultRule
      ? IntegrityMonitoringGridEvents.CLICK_ON_DEFAULT_RULE_NAME
      : IntegrityMonitoringGridEvents.CLICK_ON_CUSTOM_RULE_NAME;

    this.dom.$gridView.dispatchEvent(
      new CustomEvent(eventType, {
        detail: {
          ruleData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }
}
